import { CheckCircle as CheckCircleIcon, Error as ErrorIcon } from '@mui/icons-material';
import { parseAndValidateJson } from '../../utils/jsonExtractors';

interface JsonImpedanceInputProps {
  baselineJson: string;
  onBaselineChange: (json: string) => void;
  measurementJson: string;
  onMeasurementChange: (json: string) => void;
}

export function JsonImpedanceInput({
  baselineJson,
  onBaselineChange,
  measurementJson,
  onMeasurementChange,
}: JsonImpedanceInputProps) {
  const baselineValidation = parseAndValidateJson(baselineJson);
  const measurementValidation = parseAndValidateJson(measurementJson);

  const isBaselineValid = baselineValidation.error === null && baselineJson.trim() !== '';
  const isMeasurementValid = measurementValidation.error === null && measurementJson.trim() !== '';

  return (
    <div className="bg-white dark:bg-gray-900 rounded-lg border border-gray-200 dark:border-gray-700 p-6">
      {/* Header */}
      <div className="mb-8">
        <h3 className="text-lg font-semibold text-gray-900 dark:text-white flex items-center gap-2">
          📄 JSON Impedance Data
        </h3>
        <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
          Paste baseline and measurement JSON data. Data will be extracted automatically.
        </p>
      </div>

      {/* Baseline Section - Inline layout */}
      <div className="grid grid-cols-1 lg:grid-cols-4 gap-6 mb-8">
        {/* Left: Label & Description */}
        <div className="col-span-1 flex flex-col justify-start">
          <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 flex items-center gap-2 mb-2">
            📋 Baseline JSON
          </h4>
          <p className="text-xs text-gray-600 dark:text-gray-400 mb-3">
            Paste baseline device JSON here...
          </p>
          <div className="flex items-center gap-2">
            {baselineJson.trim() === '' ? (
              <span className="text-xs text-gray-500">Waiting for input...</span>
            ) : isBaselineValid ? (
              <>
                <CheckCircleIcon className="text-green-600" style={{ fontSize: '16px' }} />
                <span className="text-xs text-green-600">✅ Valid JSON</span>
              </>
            ) : (
              <>
                <ErrorIcon className="text-red-600" style={{ fontSize: '16px' }} />
                <span className="text-xs text-red-600">{baselineValidation.error}</span>
              </>
            )}
          </div>
        </div>

        {/* Right: Textarea */}
        <div className="col-span-1 lg:col-span-3">
          <textarea
            value={baselineJson}
            onChange={(e) => onBaselineChange(e.target.value)}
            placeholder='Paste baseline device JSON here...'
            className={`w-full p-3 font-mono text-sm border rounded-lg focus:outline-none focus:ring-2 dark:bg-gray-800 dark:text-white min-h-[250px] resize-none ${
              isBaselineValid
                ? 'border-green-300 focus:ring-green-500'
                : baselineJson.trim() === ''
                  ? 'border-gray-300 focus:ring-blue-500'
                  : 'border-red-300 focus:ring-red-500'
            }`}
          />
        </div>
      </div>

      {/* Measurement Section - Inline layout */}
      <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
        {/* Left: Label & Description */}
        <div className="col-span-1 flex flex-col justify-start">
          <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 flex items-center gap-2 mb-2">
            📋 Measurement JSON
          </h4>
          <p className="text-xs text-gray-600 dark:text-gray-400 mb-3">
            Paste measurement device JSON here...
          </p>
          <div className="flex items-center gap-2">
            {measurementJson.trim() === '' ? (
              <span className="text-xs text-gray-500">Waiting for input...</span>
            ) : isMeasurementValid ? (
              <>
                <CheckCircleIcon className="text-green-600" style={{ fontSize: '16px' }} />
                <span className="text-xs text-green-600">✅ Valid JSON</span>
              </>
            ) : (
              <>
                <ErrorIcon className="text-red-600" style={{ fontSize: '16px' }} />
                <span className="text-xs text-red-600">{measurementValidation.error}</span>
              </>
            )}
          </div>
        </div>

        {/* Right: Textarea */}
        <div className="col-span-1 lg:col-span-3">
          <textarea
            value={measurementJson}
            onChange={(e) => onMeasurementChange(e.target.value)}
            placeholder='Paste measurement device JSON here...'
            className={`w-full p-3 font-mono text-sm border rounded-lg focus:outline-none focus:ring-2 dark:bg-gray-800 dark:text-white min-h-[250px] resize-none ${
              isMeasurementValid
                ? 'border-green-300 focus:ring-green-500'
                : measurementJson.trim() === ''
                  ? 'border-gray-300 focus:ring-blue-500'
                  : 'border-red-300 focus:ring-red-500'
            }`}
          />
        </div>
      </div>

      {/* Help section */}
      <div className="mt-8 p-4 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded">
        <p className="text-sm text-blue-900 dark:text-blue-200">
          💡 <strong>Expected Structure:</strong> Each JSON should contain an <code className="bg-white dark:bg-gray-800 px-1 rounded">impedance_result</code> object with <code className="bg-white dark:bg-gray-800 px-1 rounded">frequency_Hz</code> and <code className="bg-white dark:bg-gray-800 px-1 rounded">values</code> arrays.
        </p>
      </div>
    </div>
  );
}
